import './i18n';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthCallback } from './components/AuthCallback';
import Layout from './components/Layout';
import UploadPage from './pages/UploadPage';
import HistoryPage from './pages/HistoryPage';
import AnalysisDetailPage from './pages/AnalysisDetailPage';
import DashboardPage from './pages/DashboardPage';
import LoginPage from './pages/LoginPage';
import SharedAnalysisPage from './pages/SharedAnalysisPage';
import GuidePage from './pages/GuidePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BillingProvider, useBilling } from './context/BillingContext';
import { SubscribeGate } from './components/SubscribeGate';
import { ErrorBoundary } from './components/ErrorBoundary';

const GatedApp = () => {
  const { billing, loading } = useBilling();

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(210,25%,6%)] flex items-center justify-center">
        <Loader2 size={32} className="text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (billing && !billing.access) {
    return <SubscribeGate />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<UploadPage />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/analysis/:id" element={<AnalysisDetailPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

const ProtectedApp = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(210,25%,6%)] flex items-center justify-center">
        <Loader2 size={32} className="text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <BillingProvider>
      <GatedApp />
    </BillingProvider>
  );
};

const LoginRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(210,25%,6%)] flex items-center justify-center">
        <Loader2 size={32} className="text-cyan-400 animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;
  return <LoginPage />;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AppRouter = () => {
  // Read the hash from useLocation() (reactive) - NOT window.location.hash
  const location = useLocation();
  // Synchronous check during render: process a fresh session_id BEFORE any auth guard runs
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/shared/:token" element={<SharedAnalysisPage />} />
      <Route path="/guide" element={<GuidePage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/*" element={<ProtectedApp />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App notranslate" translate="no">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
